import * as _ from "lodash-core";
import { useCallback, useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import apiClient from "../../../../../../application/apis/tenant-management/tenant-management-client";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { toast } from "react-toastify";
import { ReactSelectProps } from "@graffer-inc/console-design-kit";
import {
  gqlClient,
  gqlHeader,
  runGqlQueryV2,
} from "../../../../../../graphql/client";
import { GetQualifiedInvoiceBusinessIssuerByIdQuery } from "../../../../../../graphql/generated/types";
import {
  businessIssuerMaxSelectedGroupsCount,
  businessIssuerRegistrationNumberPrefix,
} from "../../../../../../application/domain/qualified-invoice/business-issuer";
import { Auth } from "../../../../../../application/domain/auth/auth";
import { useEnablePaymentHooks } from "./payment-settings";

type redirectToType = "list";

type Props = {
  id: string;
  orgId: string;
  auth: Auth;
};

export const useQualifiedInvoiceBusinessIssuersEditHooks = ({
  id,
  orgId,
  auth,
}: Props) => {
  const [redirectTo, setRedirectTo] = useState<redirectToType | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const [businessIssuer, setBusinessIssuer] = useState<
    | GetQualifiedInvoiceBusinessIssuerByIdQuery["invoiceBusinessIssuerByID"]
    | null
  >(null);

  const [inputGroupSearchKeyword, setInputGroupSearchKeyword] = useState("");
  const [bouncedInputGroupSearchKeyword] = useDebounce(
    inputGroupSearchKeyword,
    500,
  );

  const [orgGroupSelectOptions, setOrgGroupSelectOptions] = useState<
    ReactSelectProps<string>["options"]
  >([]);

  const [inputBusinessIssuerGroups, setInputBusinessIssuerGroups] = useState<
    ReactSelectProps<string>["value"]
  >([]);
  const [inputBusinessIssuerGroupsError, setInputBusinessIssuerGroupsError] =
    useState("");

  const isValidInput = !!inputBusinessIssuerGroupsError;
  const isUnchangedSelectedGroups = _.isEqual(
    _.sortBy(businessIssuer?.groups.map((g) => g.id)),
    _.sortBy(
      (
        inputBusinessIssuerGroups as {
          value: string;
          label: string;
        }[]
      ).map((g) => g.value),
    ),
  );
  const disabledSubmitButton =
    isLoading || isValidInput || isUnchangedSelectedGroups;

  const handleOnSetRedirectToList = useCallback(() => {
    setRedirectTo("list");
  }, []);

  const handleOnChangeGroupSearchKeyword = (input: string) => {
    setInputGroupSearchKeyword(input);
  };

  const handleOnChangeBusinessIssuerGroups = (
    value: ReactSelectProps<string>["value"],
  ) => {
    if (
      (value as { value: string; label: string }[]).length >
      businessIssuerMaxSelectedGroupsCount
    ) {
      setInputBusinessIssuerGroupsError(
        `インボイスを交付可能なグループは${businessIssuerMaxSelectedGroupsCount}件まで選択可能です。`,
      );
    } else {
      setInputBusinessIssuerGroupsError("");
    }

    setInputBusinessIssuerGroups(value);
  };

  const fetchBusinessIssuer = useCallback(async () => {
    try {
      const res = await runGqlQueryV2(() =>
        gqlClient.getQualifiedInvoiceBusinessIssuerByID(
          {
            orgId: orgId!,
            id,
          },
          gqlHeader("q"),
        ),
      );
      if (res.error) {
        throw res.error;
      }
      if (res.data?.invoiceBusinessIssuerByID) {
        setBusinessIssuer({
          ...res.data.invoiceBusinessIssuerByID,
          registrationNumber:
            res.data.invoiceBusinessIssuerByID.registrationNumber.replace(
              businessIssuerRegistrationNumberPrefix,
              "",
            ),
        });
        setInputBusinessIssuerGroups(
          res.data.invoiceBusinessIssuerByID.groups.map((g) => {
            return { value: g.id!, label: g.name };
          }),
        );
      }
    } catch (e) {
      toast.error(
        "適格請求書発行事業者の取得に失敗しました。時間を置いて再度お試しください。",
      );
      reportUnexpectedError(e);
    }
  }, [orgId, id]);

  const handleOnSubmit = async () => {
    if (
      window.confirm(
        "インボイスを交付可能なグループの設定を変更します。\nよろしいですか？",
      )
    ) {
      setIsLoading(true);
      try {
        const res = await runGqlQueryV2(() =>
          gqlClient.updateQualifiedInvoiceBusinessIssuer(
            {
              req: {
                orgId: orgId!,
                id,
                groupIds: (
                  inputBusinessIssuerGroups as {
                    value: string;
                    label: string;
                  }[]
                ).map((g) => g.value),
              },
            },
            gqlHeader("m"),
          ),
        );
        if (res.error) {
          throw res.error;
        }
        if (res.data?.updateInvoiceBusinessIssuer) {
          toast.success("適格請求書発行事業者を更新しました。");
          handleOnSetRedirectToList();
        }
      } catch (e) {
        toast.error(
          "適格請求書発行事業の更新に失敗しました。時間を置いて再度お試しください。",
        );
        reportUnexpectedError(e);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const fetchOrgGroups = useCallback(
    async (searchKeyword: string) => {
      try {
        const { data: groups } = await apiClient.fetchGroupsByOrganizationId(
          orgId!,
          1,
          50,
          {
            name: searchKeyword,
          },
        );

        const res = await runGqlQueryV2(() =>
          gqlClient.getInvoiceBusinessIssuerGroupRelationsByOrgId(
            { orgId: orgId! },
            gqlHeader("q"),
          ),
        );
        if (res.error) {
          throw res.error;
        }
        if (res.data?.invoiceBusinessIssuerGroupRelationsByOrgID) {
          const unSelectedOrgGroups = groups.filter((g) => {
            return (
              businessIssuer?.groups.find((group) => group.id === g.id) ||
              !res.data?.invoiceBusinessIssuerGroupRelationsByOrgID.find(
                (r) => r.groupId === g.id,
              )
            );
          });

          const unSelectedOrgGroupOptions = unSelectedOrgGroups.map((g) => {
            return { value: g.id!, label: g.name };
          });

          setOrgGroupSelectOptions(unSelectedOrgGroupOptions);
        }
      } catch (e) {
        reportUnexpectedError(e);
        toast.error(
          "インボイスを交付可能なグループの選択肢の取得に失敗しました。時間を置いて再度お試しください。",
        );
      }
    },
    [orgId, businessIssuer],
  );

  const { enablePayment } = useEnablePaymentHooks({
    orgId,
  });

  useEffect(() => {
    if (
      auth.hasPermissions("invoice_business_issuers.update") &&
      businessIssuer
    ) {
      fetchOrgGroups(bouncedInputGroupSearchKeyword);
    }
  }, [auth, bouncedInputGroupSearchKeyword, businessIssuer, fetchOrgGroups]);

  useEffect(() => {
    if (auth.hasPermissions("invoice_business_issuers.update")) {
      fetchBusinessIssuer();
    }
  }, [fetchBusinessIssuer, auth]);

  return {
    enablePayment,
    redirectTo,
    isLoading,
    disabledSubmitButton,
    inputBusinessIssuerGroups,
    inputBusinessIssuerGroupsError,
    businessIssuer,
    orgGroupSelectOptions,
    handleOnSetRedirectToList,
    handleOnChangeGroupSearchKeyword,
    handleOnChangeBusinessIssuerGroups,
    handleOnSubmit,
  };
};
